import React, { useState } from 'react';
import styled from 'styled-components';
import { FiArrowRight, FiChevronDown, FiChevronUp, FiList, FiLock, FiTrendingUp } from 'react-icons/fi';
import { useSurveyClassLink } from './useSurveyClassLink';
import { PageContainer, HeroTitle, HeroSubtitle, Card, PrimaryButton } from '../../../components/surveys/surveyTheme.styles';

// ~30 seconds/question, rounded up to the nearest minute (never shown as 0).
const estimateMinutes = (questionCount) => Math.max(1, Math.round(questionCount * 0.5));

// A title like "Foundations Program: End-of-Year Survey" splits into a
// short program label (folded into the subtitle, alongside the course name)
// and the actual heading — titles without a colon fall back to showing the
// whole thing as the heading, same as before.
const splitTitle = (title) => {
  const separatorIndex = title.indexOf(':');
  if (separatorIndex === -1) return { prefix: null, main: title };
  return { prefix: title.slice(0, separatorIndex).trim(), main: title.slice(separatorIndex + 1).trim() };
};

const InfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 1.5rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 230px;
  text-align: left;

  @media (max-width: 480px) {
    max-width: none;
    width: 100%;
  }
`;

const InfoIcon = styled.div`
  flex: none;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(216, 184, 137, 0.18);
  color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 480px) {
    width: 2.25rem;
    height: 2.25rem;
  }
`;

const InfoText = styled.div`
  font-size: 0.85rem;

  strong {
    display: block;
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 2px;
  }

  span {
    color: var(--text-muted);
    line-height: 1.4;
  }
`;

const WhyLoginToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 1rem;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.85rem;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    color: var(--primary-color);
  }
`;

export default function SurveyClassLink() {
  const { authLoading, error, goToLogin, loading, preview, resolving, user } = useSurveyClassLink();
  const [whyLoginOpen, setWhyLoginOpen] = useState(false);

  if (loading || authLoading || resolving) {
    return <PageContainer $narrow><p>Loading…</p></PageContainer>;
  }

  const { prefix: titlePrefix, main: titleMain } = preview ? splitTitle(preview.title) : {};
  const subtitle = preview ? [titlePrefix, preview.course_name].filter(Boolean).join(' · ') : '';
  const minutes = preview?.question_count ? estimateMinutes(preview.question_count) : 0;

  return (
    <PageContainer $narrow style={{ textAlign: 'center' }}>
      <Card>
        {preview && (
          <>
            <HeroTitle>{titleMain}</HeroTitle>
            {subtitle && <HeroSubtitle>{subtitle}</HeroSubtitle>}
            {preview.description && (
              <p style={{ marginTop: 12, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{preview.description}</p>
            )}
          </>
        )}

        {error && <p style={{ color: 'var(--error-color)', marginTop: 16, fontSize: '0.85rem' }}>{error}</p>}

        {!user && !error && (
          <>
            <InfoRow>
              {preview.question_count > 0 && (
                <InfoItem>
                  <InfoIcon><FiList size={18} /></InfoIcon>
                  <InfoText>
                    <strong>{preview.question_count} question{preview.question_count === 1 ? '' : 's'}</strong>
                    <span>About {minutes} minute{minutes === 1 ? '' : 's'}</span>
                  </InfoText>
                </InfoItem>
              )}

              {preview.is_anonymous && (
                <InfoItem>
                  <InfoIcon><FiLock size={18} /></InfoIcon>
                  <InfoText>
                    <strong>Your responses are anonymous.</strong>
                    <span>Your login is used only to confirm survey completion and is not linked to your responses.</span>
                  </InfoText>
                </InfoItem>
              )}

              <InfoItem>
                <InfoIcon><FiTrendingUp size={18} /></InfoIcon>
                <InfoText>
                  <strong>Your feedback matters.</strong>
                  <span>It helps shape what comes next.</span>
                </InfoText>
              </InfoItem>
            </InfoRow>

            <PrimaryButton style={{ marginTop: 24 }} onClick={goToLogin}>
              Continue to Survey <FiArrowRight size={16} />
            </PrimaryButton>

            <div>
              <WhyLoginToggle type="button" onClick={() => setWhyLoginOpen(open => !open)}>
                Why do I need to log in? {whyLoginOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
              </WhyLoginToggle>
              {whyLoginOpen && (
                <p style={{ margin: '8px auto 0', maxWidth: 420, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Logging in lets us confirm you completed the survey and take you straight here next time — it has no effect on whether your answers are linked to your identity.
                </p>
              )}
            </div>
          </>
        )}
      </Card>
    </PageContainer>
  );
}
